import { Check, CircleAlert, X } from 'lucide-react';
import { type ReactNode } from 'react';

import { Spinner } from '@apps/web/components/ui/spinner';
import { ResolvedReferenceText } from '@apps/web/features/phoebe_chat_v3/ui/primitives/reference_badge';
import { cn } from '@apps/web/lib/utils';

// Each receipt keeps the surface of the card it resolves. Only its content
// and height contract; changing state never changes the card's identity.
export const CARD_SURFACES = {
    question: 'bg-muted rounded-lg',
    communication: 'bg-card rounded-lg border',
    assignment:
        'bg-card rounded-lg border shadow-[0_1px_2px_rgba(0,0,0,0.04),0_4px_14px_rgba(0,0,0,0.05)]',
    outreach: 'bg-card rounded-lg border shadow-[0_1px_3px_rgba(0,0,0,0.07)]',
    action: 'bg-card rounded-lg border',
    table: 'bg-card rounded-lg border',
} as const;
export type ReceiptSurface = keyof typeof CARD_SURFACES;
export const RECEIPT_PADDING: Record<ReceiptSurface, string> = {
    question: 'px-[22px] py-4',
    communication: 'px-3.5 py-3',
    assignment: 'px-4 py-3.5',
    outreach: 'px-[11px] py-2.5',
    action: 'px-3 py-2.5',
    table: 'p-3',
};

export type ReceiptStatus =
    | 'running'
    | 'succeeded'
    | 'failed'
    | 'denied'
    | 'skipped';

/** Shared visual language for completed or retired V3 cards. Status copy
 * remains caller-owned so approval never implies successful execution. */
export function CompactReceipt({
    title,
    summary,
    status,
    marker,
    action,
    children,
    ariaLabel,
    embedded = false,
    surface = 'action',
    statusLabel,
}: Readonly<{
    readonly title: string;
    readonly summary?: ReactNode;
    readonly status: ReceiptStatus;
    readonly marker?: ReactNode;
    readonly action?: ReactNode;
    readonly children?: ReactNode;
    readonly ariaLabel?: string;
    readonly embedded?: boolean;
    readonly surface?: ReceiptSurface;
    readonly statusLabel?: string;
}>): ReactNode {
    const label =
        statusLabel ??
        {
            running: 'In progress',
            succeeded: 'Completed',
            failed: 'Failed',
            denied: 'Rejected',
            skipped: 'Skipped',
        }[status];
    const indicator = (
        <span
            role="img"
            aria-label={label}
            title={label}
            className={cn(
                'inline-flex h-(--text-v3-chat--line-height) shrink-0 items-center justify-center gap-1.5',
                status === 'skipped' ? 'text-v3-chat-meta font-normal' : 'w-4',
                status === 'failed'
                    ? 'text-agent-negative'
                    : 'text-muted-foreground',
            )}
        >
            {status === 'skipped' && <span>Skipped</span>}
            {status === 'running' ? (
                <Spinner size="xs" className="motion-reduce:animate-none" />
            ) : status === 'failed' ? (
                <CircleAlert aria-hidden={true} className="size-4" />
            ) : status === 'succeeded' ? (
                <Check aria-hidden={true} className="size-4" />
            ) : status === 'denied' ? (
                <X aria-hidden={true} className="size-4" />
            ) : null}
        </span>
    );
    return (
        <div
            data-receipt-status={status}
            data-receipt-surface={surface}
            className={cn(
                'text-foreground w-full min-w-0 [overflow-wrap:anywhere] [&_.text-muted-foreground]:text-foreground/65',
                !embedded && CARD_SURFACES[surface],
                RECEIPT_PADDING[surface],
            )}
        >
            <div role="status" aria-label={ariaLabel}>
                {/* The status sits against the whole receipt, not its
                    first line, so a wrapping title cannot strand it. */}
                <div className="flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                        <h3 className="text-v3-chat m-0 font-semibold">
                            <ResolvedReferenceText text={title} />
                        </h3>
                        {summary !== undefined && (
                            <div className="text-v3-chat mt-3 flex items-start gap-3">
                                {marker !== undefined && (
                                    <span
                                        aria-hidden={true}
                                        className="text-muted-foreground text-v3-chat-meta w-4 shrink-0 text-center leading-(--text-v3-chat--line-height) font-medium"
                                    >
                                        {marker}
                                    </span>
                                )}
                                <div
                                    className={cn(
                                        'min-w-0 flex-1',
                                        (status === 'denied' ||
                                            status === 'skipped') &&
                                            'text-muted-foreground',
                                    )}
                                >
                                    {summary}
                                </div>
                            </div>
                        )}
                    </div>
                    {indicator}
                </div>
            </div>
            {children}
            {action !== undefined && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                    {action}
                </div>
            )}
        </div>
    );
}
